import type { SupportedLocale } from '@erp/shared';

const de = {
  selectionHint: 'Wähle Quellen der angezeigten Seite aus, höchstens {{limit}} pro Vorschau.',
  createPreview: 'Vorschau erstellen',
  title: 'Aktenzuordnung',
  recordTitle: 'E-Mails',
  recordHint:
    'Zugeordnete Nachrichten aus freigegebenen Quellen. Private Postfachkopien bleiben geschützt.',
  recordEmpty: 'Keine für dich sichtbaren E-Mails zu dieser Auswahl.',
  businessSource: 'Für die Akte freigegebene Nachricht',
  identityHint: 'Eine Adressübereinstimmung ist kein Nachweis der Identität des Absenders.',
  decisionHint:
    'Die Entscheidung gilt für diese Nachricht und bleibt bei späteren Abrufen erhalten. Andere Nachrichten der Unterhaltung werden nicht freigegeben.',
  empty: 'Keine sichtbare Zuordnung. Eine erneute Prüfung berücksichtigt aktuelle Stammdaten.',
  loadFailed: 'Zuordnungen konnten nicht geladen werden oder sind nicht mehr freigegeben.',
  saved: 'Entscheidung gespeichert.',
  saveFailed: 'Speichern fehlgeschlagen. Deine Eingaben bleiben erhalten.',
  checkFields: 'Bitte prüfe die markierten Angaben.',
  reason: 'Begründung',
  reasonRequired: 'Bitte mindestens drei Zeichen als Begründung eingeben.',
  partnerSearch: 'Geschäftspartner suchen',
  partner: 'Geschäftspartner',
  choosePartner: 'Bitte Geschäftspartner auswählen',
  partnerOnly: 'Nur Geschäftspartner',
  contact: 'Ansprechpartner',
  allContacts: 'Alle Ansprechpartner',
  targetInvalid: 'Bitte genau einen passenden Geschäftspartner bzw. Ansprechpartner auswählen.',
  add: 'Zuordnung ergänzen',
  confirmTarget: 'Zuordnung bestätigen',
  remove: 'Zuordnung entfernen',
  bccOnly: 'Vertrauliche BCC-Zuordnung; keine Freigabe über andere Akten.',
  irrelevant: 'Geschäftlich irrelevant',
  rematch: 'Mit aktuellen Adressen prüfen',
  rematchFailed: 'Erneute Prüfung nicht möglich. Berechtigungen prüfen und Vorschau erneuern.',
  rematched: 'Geprüfte Zuordnungen übernommen.',
  previewHint:
    'Vorschau ausschließlich für bereits gespeicherte Quellen. Manuelle Entscheidungen bleiben erhalten; es wird kein Postfach abgerufen.',
  previewCounts:
    '{{linked}} eindeutige Zuordnungen · {{ambiguous}} mehrdeutige Kandidaten · {{preserved}} manuelle Entscheidungen bleiben erhalten',
  apply: 'Geprüfte Vorschau übernehmen',
  stale: 'Die Vorschau oder der Datensatz ist veraltet. Bitte neu laden und erneut prüfen.',
  draftRelease: 'Entwürfe können nicht als versendete Aktenkommunikation freigegeben werden.',
  newTargetPermission:
    'Neue Aktenziele benötigen volle Freigabe der vertraulichen Empfängerdaten. Sichtbare Kandidaten können weiterhin bestätigt werden.',
  tooManyCandidates:
    'Zu viele Stammdatentreffer. Bitte die doppelt verwendeten E-Mail-Adressen bereinigen.',
  fieldsTitle: 'Zusatzfelder als E-Mail-Adressen verwenden',
  fieldsHint:
    'Nur ausdrücklich freigegebene, veröffentlichte Textfelder mit genau einer E-Mail-Adresse werden abgeglichen. Neue Feldversionen benötigen eine erneute Freigabe. Feldrechte bleiben wirksam.',
  fieldsEmpty: 'Keine zugänglichen veröffentlichten Textfelder verfügbar.',
  enableField: 'Als E-Mail-Feld freigeben',
  disableField: 'E-Mail-Abgleich ausschalten',
  fieldEnabled: 'Freigegebene aktuelle Feldversion',
  fieldDisabled: 'Nicht für E-Mail-Abgleich freigegeben',
  fieldOutdated: 'Neue Feldversion: erneute Freigabe erforderlich',
  actions: {
    ADD: 'Zuordnung ergänzen',
    REMOVE: 'Zuordnung entfernen',
    RELEASE: 'Für die Akte freigeben',
    PRIVATE: 'Aktenfreigabe zurücknehmen',
    IRRELEVANT: 'Als irrelevant kennzeichnen',
    RELEVANT: 'Wieder als geschäftlich kennzeichnen',
  },
  states: {
    LINKED: 'Zugeordnet',
    AMBIGUOUS: 'Entscheidung erforderlich',
    REMOVED: 'Bewusst entfernt',
  },
  visibility: {
    PRIVATE: 'Privat im berechtigten Postfach',
    BUSINESS: 'Für berechtigte Aktenleser freigegeben',
    EXCLUDED: 'Von der Aktenanzeige ausgeschlossen',
  },
  reasons: {
    PARTNER_EMAIL: 'Adresse entspricht der E-Mail-Adresse des Geschäftspartners.',
    CONTACT_EMAIL: 'Adresse entspricht der E-Mail-Adresse des Ansprechpartners.',
    ADDRESS_EMAIL: 'Adresse entspricht dem lokalen Kontakt der abweichenden Adresse.',
    EXTRA_EMAIL: 'Adresse entspricht einem ausdrücklich freigegebenen E-Mail-Zusatzfeld.',
    MANUAL: 'Bewusste manuelle Entscheidung.',
  },
  entities: {
    CUSTOMER: 'Geschäftspartner',
    CONTACT: 'Ansprechpartner',
    ADDRESS: 'Abweichende Adresse',
  },
};
type Copy = {
  [K in keyof typeof de]: (typeof de)[K] extends string
    ? string
    : { [P in keyof (typeof de)[K]]: string };
};
const en: Copy = {
  selectionHint: 'Select sources from the displayed page, at most {{limit}} per preview.',
  createPreview: 'Create preview',
  title: 'Record assignment',
  recordTitle: 'Emails',
  recordHint: 'Assigned messages from released sources. Private mailbox copies remain protected.',
  recordEmpty: 'No emails are visible to you for this selection.',
  businessSource: 'Message released to the business record',
  identityHint: 'An address match does not verify the identity of the sender.',
  decisionHint:
    'This decision applies to this message and survives later synchronization. Other conversation messages are not released.',
  empty: 'No visible assignment. Rechecking uses current master data.',
  loadFailed: 'Assignments could not be loaded or access has been revoked.',
  saved: 'Decision saved.',
  saveFailed: 'Saving failed. Your input has been retained.',
  checkFields: 'Please check the highlighted fields.',
  reason: 'Reason',
  reasonRequired: 'Enter a reason with at least three characters.',
  partnerSearch: 'Search business partners',
  partner: 'Business partner',
  choosePartner: 'Select a business partner',
  partnerOnly: 'Business partner only',
  contact: 'Contact person',
  allContacts: 'All contact persons',
  targetInvalid: 'Select one valid business partner or contact person.',
  add: 'Add assignment',
  confirmTarget: 'Confirm assignment',
  remove: 'Remove assignment',
  bccOnly: 'Confidential BCC assignment; not released through other records.',
  irrelevant: 'Not business relevant',
  rematch: 'Check current addresses',
  rematchFailed: 'Rechecking failed. Check your permissions and create a new preview.',
  rematched: 'Reviewed assignments applied.',
  previewHint:
    'Preview for stored sources only. Manual decisions are retained; no mailbox is contacted.',
  previewCounts:
    '{{linked}} unique assignments · {{ambiguous}} ambiguous candidates · {{preserved}} manual decisions retained',
  apply: 'Apply reviewed preview',
  stale: 'The preview or record is outdated. Reload and check again.',
  draftRelease: 'Drafts cannot be released as sent business communication.',
  newTargetPermission:
    'New record targets require full access to confidential recipient information. Visible candidates can still be confirmed.',
  tooManyCandidates: 'Too many master data matches. Review duplicate email addresses.',
  fieldsTitle: 'Use custom fields as email addresses',
  fieldsHint:
    'Only explicitly approved published text fields containing a single email address are matched. New field revisions require renewed approval. Field permissions remain effective.',
  fieldsEmpty: 'No accessible published text fields are available.',
  enableField: 'Approve as email field',
  disableField: 'Disable email matching',
  fieldEnabled: 'Current field revision approved',
  fieldDisabled: 'Not approved for email matching',
  fieldOutdated: 'New field revision: renewed approval required',
  actions: {
    ADD: 'Add assignment',
    REMOVE: 'Remove assignment',
    RELEASE: 'Release to business record',
    PRIVATE: 'Withdraw record release',
    IRRELEVANT: 'Mark as irrelevant',
    RELEVANT: 'Mark as business relevant again',
  },
  states: { LINKED: 'Assigned', AMBIGUOUS: 'Decision required', REMOVED: 'Deliberately removed' },
  visibility: {
    PRIVATE: 'Private in the authorized mailbox',
    BUSINESS: 'Released to authorized record readers',
    EXCLUDED: 'Excluded from business records',
  },
  reasons: {
    PARTNER_EMAIL: 'Address matches the business partner email address.',
    CONTACT_EMAIL: 'Address matches the contact person email address.',
    ADDRESS_EMAIL: 'Address matches the local contact of the alternative address.',
    EXTRA_EMAIL: 'Address matches an explicitly approved email custom field.',
    MANUAL: 'Explicit manual decision.',
  },
  entities: {
    CUSTOMER: 'Business partner',
    CONTACT: 'Contact person',
    ADDRESS: 'Alternative address',
  },
};
const fr: Copy = {
  selectionHint: 'Sélectionnez les sources de la page affichée, au maximum {{limit}} par aperçu.',
  createPreview: 'Créer un aperçu',
  title: 'Affectation au dossier',
  recordTitle: 'E-mails',
  recordHint:
    'Messages affectés provenant de sources partagées. Les copies des boîtes privées restent protégées.',
  recordEmpty: 'Aucun e-mail visible pour cette sélection.',
  businessSource: 'Message partagé dans le dossier',
  identityHint: 'Une correspondance d’adresse ne confirme pas l’identité de l’expéditeur.',
  decisionHint:
    'Cette décision concerne ce message et reste valable après synchronisation. Les autres messages de la conversation ne sont pas partagés.',
  empty: 'Aucune affectation visible. La nouvelle vérification utilise les coordonnées actuelles.',
  loadFailed: 'Chargement impossible ou accès retiré.',
  saved: 'Décision enregistrée.',
  saveFailed: 'Échec de l’enregistrement. Vos saisies sont conservées.',
  checkFields: 'Vérifiez les champs signalés.',
  reason: 'Motif',
  reasonRequired: 'Saisissez un motif d’au moins trois caractères.',
  partnerSearch: 'Rechercher un partenaire',
  partner: 'Partenaire',
  choosePartner: 'Sélectionner un partenaire',
  partnerOnly: 'Partenaire uniquement',
  contact: 'Interlocuteur',
  allContacts: 'Tous les interlocuteurs',
  targetInvalid: 'Sélectionnez un partenaire ou un interlocuteur valide.',
  add: 'Ajouter une affectation',
  confirmTarget: 'Confirmer l’affectation',
  remove: 'Retirer l’affectation',
  bccOnly: 'Affectation CCI confidentielle, non partagée dans les autres dossiers.',
  irrelevant: 'Sans intérêt professionnel',
  rematch: 'Vérifier les adresses actuelles',
  rematchFailed: 'Vérification impossible. Contrôlez les droits et renouvelez l’aperçu.',
  rematched: 'Affectations vérifiées appliquées.',
  previewHint:
    'Aperçu limité aux sources enregistrées. Les décisions manuelles sont conservées et aucune boîte n’est contactée.',
  previewCounts:
    '{{linked}} affectations certaines · {{ambiguous}} candidats ambigus · {{preserved}} décisions manuelles conservées',
  apply: 'Appliquer l’aperçu vérifié',
  stale: 'L’aperçu ou le dossier est obsolète. Rechargez puis vérifiez.',
  draftRelease: 'Un brouillon ne peut pas être partagé comme communication envoyée.',
  newTargetPermission:
    'Les nouveaux dossiers nécessitent l’accès complet aux destinataires confidentiels. Les candidats visibles peuvent toujours être confirmés.',
  tooManyCandidates:
    'Trop de correspondances. Vérifiez les adresses e-mail utilisées plusieurs fois.',
  fieldsTitle: 'Utiliser des champs supplémentaires comme adresses e-mail',
  fieldsHint:
    'Seuls les champs texte publiés, explicitement autorisés et contenant une seule adresse e-mail sont comparés. Toute nouvelle révision nécessite une nouvelle autorisation. Les droits des champs restent applicables.',
  fieldsEmpty: 'Aucun champ texte publié accessible.',
  enableField: 'Autoriser comme champ e-mail',
  disableField: 'Désactiver la comparaison e-mail',
  fieldEnabled: 'Révision actuelle autorisée',
  fieldDisabled: 'Non autorisé pour la comparaison e-mail',
  fieldOutdated: 'Nouvelle révision : autorisation à renouveler',
  actions: {
    ADD: 'Ajouter une affectation',
    REMOVE: 'Retirer l’affectation',
    RELEASE: 'Partager dans le dossier',
    PRIVATE: 'Retirer le partage du dossier',
    IRRELEVANT: 'Marquer sans intérêt',
    RELEVANT: 'Marquer à nouveau professionnel',
  },
  states: { LINKED: 'Affecté', AMBIGUOUS: 'Décision nécessaire', REMOVED: 'Retiré volontairement' },
  visibility: {
    PRIVATE: 'Privé dans la boîte autorisée',
    BUSINESS: 'Partagé avec les lecteurs autorisés du dossier',
    EXCLUDED: 'Exclu des dossiers',
  },
  reasons: {
    PARTNER_EMAIL: 'L’adresse correspond à l’e-mail du partenaire.',
    CONTACT_EMAIL: 'L’adresse correspond à l’e-mail de l’interlocuteur.',
    ADDRESS_EMAIL: 'L’adresse correspond au contact local de l’adresse différente.',
    EXTRA_EMAIL: 'L’adresse correspond à un champ e-mail explicitement autorisé.',
    MANUAL: 'Décision manuelle explicite.',
  },
  entities: { CUSTOMER: 'Partenaire', CONTACT: 'Interlocuteur', ADDRESS: 'Adresse différente' },
};
const es: Copy = {
  selectionHint: 'Seleccione fuentes de la página mostrada, hasta {{limit}} por vista previa.',
  createPreview: 'Crear vista previa',
  title: 'Asignación al expediente',
  recordTitle: 'Correos',
  recordHint:
    'Mensajes asignados de fuentes compartidas. Las copias de buzones privados siguen protegidas.',
  recordEmpty: 'No hay correos visibles para esta selección.',
  businessSource: 'Mensaje compartido en el expediente',
  identityHint: 'Una coincidencia de dirección no confirma la identidad del remitente.',
  decisionHint:
    'Esta decisión afecta a este mensaje y se conserva tras sincronizar. No se comparten otros mensajes de la conversación.',
  empty: 'No hay asignación visible. La revisión utiliza los datos maestros actuales.',
  loadFailed: 'No se pudieron cargar las asignaciones o se retiró el acceso.',
  saved: 'Decisión guardada.',
  saveFailed: 'No se pudo guardar. Se conservan los datos introducidos.',
  checkFields: 'Revise los campos marcados.',
  reason: 'Motivo',
  reasonRequired: 'Introduzca un motivo de al menos tres caracteres.',
  partnerSearch: 'Buscar socio comercial',
  partner: 'Socio comercial',
  choosePartner: 'Seleccione un socio comercial',
  partnerOnly: 'Solo socio comercial',
  contact: 'Persona de contacto',
  allContacts: 'Todas las personas de contacto',
  targetInvalid: 'Seleccione un socio comercial o contacto válido.',
  add: 'Añadir asignación',
  confirmTarget: 'Confirmar asignación',
  remove: 'Eliminar asignación',
  bccOnly: 'Asignación CCO confidencial; no se comparte a través de otros expedientes.',
  irrelevant: 'Sin relevancia comercial',
  rematch: 'Comprobar direcciones actuales',
  rematchFailed: 'No se pudo revisar. Compruebe los permisos y renueve la vista previa.',
  rematched: 'Asignaciones revisadas aplicadas.',
  previewHint:
    'Vista previa solo de fuentes guardadas. Se conservan las decisiones manuales y no se contacta ningún buzón.',
  previewCounts:
    '{{linked}} asignaciones inequívocas · {{ambiguous}} candidatos ambiguos · {{preserved}} decisiones manuales conservadas',
  apply: 'Aplicar vista previa revisada',
  stale: 'La vista previa o el registro están desactualizados. Recargue y vuelva a revisar.',
  draftRelease: 'Los borradores no se pueden compartir como comunicaciones enviadas.',
  newTargetPermission:
    'Los nuevos expedientes requieren acceso completo a los destinatarios confidenciales. Los candidatos visibles se pueden confirmar.',
  tooManyCandidates: 'Demasiadas coincidencias. Revise las direcciones de correo duplicadas.',
  fieldsTitle: 'Usar campos adicionales como direcciones de correo',
  fieldsHint:
    'Solo se comparan los campos de texto publicados, autorizados expresamente y con una única dirección de correo. Las nuevas revisiones requieren otra autorización. Se mantienen los permisos del campo.',
  fieldsEmpty: 'No hay campos de texto publicados accesibles.',
  enableField: 'Autorizar como campo de correo',
  disableField: 'Desactivar coincidencias de correo',
  fieldEnabled: 'Revisión actual autorizada',
  fieldDisabled: 'No autorizado para coincidencias de correo',
  fieldOutdated: 'Nueva revisión: requiere otra autorización',
  actions: {
    ADD: 'Añadir asignación',
    REMOVE: 'Eliminar asignación',
    RELEASE: 'Compartir en el expediente',
    PRIVATE: 'Retirar acceso del expediente',
    IRRELEVANT: 'Marcar como irrelevante',
    RELEVANT: 'Restablecer relevancia comercial',
  },
  states: { LINKED: 'Asignado', AMBIGUOUS: 'Requiere decisión', REMOVED: 'Eliminado expresamente' },
  visibility: {
    PRIVATE: 'Privado en el buzón autorizado',
    BUSINESS: 'Compartido con lectores autorizados del expediente',
    EXCLUDED: 'Excluido de los expedientes',
  },
  reasons: {
    PARTNER_EMAIL: 'La dirección coincide con el correo del socio comercial.',
    CONTACT_EMAIL: 'La dirección coincide con el correo de la persona de contacto.',
    ADDRESS_EMAIL: 'La dirección coincide con el contacto local de la dirección alternativa.',
    EXTRA_EMAIL: 'La dirección coincide con un campo de correo autorizado expresamente.',
    MANUAL: 'Decisión manual expresa.',
  },
  entities: {
    CUSTOMER: 'Socio comercial',
    CONTACT: 'Persona de contacto',
    ADDRESS: 'Dirección alternativa',
  },
};
const tr: Copy = {
  selectionHint: 'Gösterilen sayfadan önizleme başına en fazla {{limit}} kaynak seçin.',
  createPreview: 'Önizleme oluştur',
  title: 'Kayda ilişkilendirme',
  recordTitle: 'E-postalar',
  recordHint:
    'Paylaşılan kaynaklardan ilişkilendirilen iletiler. Özel posta kutusu kopyaları korunur.',
  recordEmpty: 'Bu seçim için görebileceğiniz e-posta yok.',
  businessSource: 'İş ortağı kaydında paylaşılan ileti',
  identityHint: 'Adres eşleşmesi gönderenin kimliğini doğrulamaz.',
  decisionHint:
    'Bu karar yalnızca bu ileti için geçerlidir ve sonraki eşitlemelerde korunur. Görüşmenin diğer iletileri paylaşılmaz.',
  empty: 'Görünür ilişkilendirme yok. Yeniden kontrol güncel ana verileri kullanır.',
  loadFailed: 'İlişkilendirmeler yüklenemedi veya erişim kaldırıldı.',
  saved: 'Karar kaydedildi.',
  saveFailed: 'Kayıt başarısız. Girdiğiniz bilgiler korunuyor.',
  checkFields: 'İşaretli alanları kontrol edin.',
  reason: 'Gerekçe',
  reasonRequired: 'En az üç karakterlik bir gerekçe girin.',
  partnerSearch: 'İş ortağı ara',
  partner: 'İş ortağı',
  choosePartner: 'İş ortağı seçin',
  partnerOnly: 'Yalnızca iş ortağı',
  contact: 'İlgili kişi',
  allContacts: 'Tüm ilgili kişiler',
  targetInvalid: 'Geçerli bir iş ortağı veya ilgili kişi seçin.',
  add: 'İlişkilendirme ekle',
  confirmTarget: 'İlişkilendirmeyi onayla',
  remove: 'İlişkilendirmeyi kaldır',
  bccOnly: 'Gizli alıcı ilişkilendirmesi; diğer kayıtlarda paylaşılmaz.',
  irrelevant: 'İşle ilgili değil',
  rematch: 'Güncel adreslerle kontrol et',
  rematchFailed: 'Yeniden kontrol yapılamadı. Yetkileri kontrol edip önizlemeyi yenileyin.',
  rematched: 'İncelenen ilişkilendirmeler uygulandı.',
  previewHint:
    'Yalnızca kaydedilmiş kaynakların önizlemesi. Elle verilen kararlar korunur ve posta kutusuna bağlanılmaz.',
  previewCounts:
    '{{linked}} kesin ilişkilendirme · {{ambiguous}} belirsiz aday · {{preserved}} elle verilen karar korunacak',
  apply: 'İncelenen önizlemeyi uygula',
  stale: 'Önizleme veya kayıt güncel değil. Yeniden yükleyip kontrol edin.',
  draftRelease: 'Taslaklar gönderilmiş iş yazışması olarak paylaşılamaz.',
  newTargetPermission:
    'Yeni kayıt hedefleri için gizli alıcı bilgilerine tam erişim gerekir. Görünür adaylar yine onaylanabilir.',
  tooManyCandidates:
    'Çok fazla ana veri eşleşmesi var. Yinelenen e-posta adreslerini gözden geçirin.',
  fieldsTitle: 'Ek alanları e-posta adresi olarak kullan',
  fieldsHint:
    'Yalnızca açıkça onaylanmış, yayımlanmış ve tek bir e-posta adresi içeren metin alanları eşleştirilir. Yeni alan sürümleri tekrar onay gerektirir. Alan yetkileri geçerliliğini korur.',
  fieldsEmpty: 'Erişilebilir yayımlanmış metin alanı yok.',
  enableField: 'E-posta alanı olarak onayla',
  disableField: 'E-posta eşleştirmesini kapat',
  fieldEnabled: 'Güncel alan sürümü onaylı',
  fieldDisabled: 'E-posta eşleştirmesi için onaylı değil',
  fieldOutdated: 'Yeni alan sürümü: tekrar onay gerekli',
  actions: {
    ADD: 'İlişkilendirme ekle',
    REMOVE: 'İlişkilendirmeyi kaldır',
    RELEASE: 'İş ortağı kaydında paylaş',
    PRIVATE: 'Kayıt paylaşımını geri al',
    IRRELEVANT: 'İşle ilgisiz olarak işaretle',
    RELEVANT: 'Tekrar işle ilgili olarak işaretle',
  },
  states: {
    LINKED: 'İlişkilendirildi',
    AMBIGUOUS: 'Karar gerekli',
    REMOVED: 'Bilinçli olarak kaldırıldı',
  },
  visibility: {
    PRIVATE: 'Yetkili posta kutusunda özel',
    BUSINESS: 'Yetkili kayıt okuyucularıyla paylaşılıyor',
    EXCLUDED: 'İş ortağı kayıtlarından hariç',
  },
  reasons: {
    PARTNER_EMAIL: 'Adres, iş ortağının e-posta adresiyle eşleşiyor.',
    CONTACT_EMAIL: 'Adres, ilgili kişinin e-posta adresiyle eşleşiyor.',
    ADDRESS_EMAIL: 'Adres, farklı adresin yerel ilgili kişisiyle eşleşiyor.',
    EXTRA_EMAIL: 'Adres, açıkça onaylanmış bir ek e-posta alanıyla eşleşiyor.',
    MANUAL: 'Açık elle verilen karar.',
  },
  entities: { CUSTOMER: 'İş ortağı', CONTACT: 'İlgili kişi', ADDRESS: 'Farklı adres' },
};
export const centralMailAssignmentTranslations: Record<SupportedLocale, Copy> = {
  de,
  en,
  fr,
  es,
  tr,
};
